import { StatusCode } from '../helpers/enums';
import { PayPalApi } from '../util/api';

import { storableError } from '../util/errors';
import * as log from '../util/log';

// ================ Action types ================ //

export const ONBOARDING_LINK_REQUEST = 'app/paypal/ONBOARDING_LINK_REQUEST';
export const ONBOARDING_LINK_SUCCESS = 'app/paypal/ONBOARDING_LINK_SUCCESS';
export const ONBOARDING_LINK_ERROR = 'app/paypal/ONBOARDING_LINK_ERROR';

export const MERCHANT_SIGNUP_STATUS_REQUEST = 'app/paypal/MERCHANT_SIGNUP_STATUS_REQUEST';
export const MERCHANT_SIGNUP_STATUS_SUCCESS = 'app/paypal/MERCHANT_SIGNUP_STATUS_SUCCESS';
export const MERCHANT_SIGNUP_STATUS_ERROR = 'app/paypal/MERCHANT_SIGNUP_STATUS_ERROR';

export const AUTHORIZE_ORDER_REQUEST = 'app/paypal/AUTHORIZE_ORDER_REQUEST';
export const AUTHORIZE_ORDER_SUCCESS = 'app/paypal/AUTHORIZE_ORDER_SUCCESS';
export const AUTHORIZE_ORDER_ERROR = 'app/paypal/AUTHORIZE_ORDER_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchOnboardingLinkInProgress: false,
  fetchOnboardingLinkError: null,
  onboardingLink: null,
  fetchPaypalMerchantInProgress: false,
  fetchPaypalMerchantError: null,
  paypalMerchant: null,
  authorizeOrderInProgress: false,
  authorizeOrderError: null,
  paypalAuthorizationData: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ONBOARDING_LINK_REQUEST:
      return { ...state, fetchOnboardingLinkError: null, fetchOnboardingLinkInProgress: true };
    case ONBOARDING_LINK_SUCCESS:
      return {
        ...state,
        fetchOnboardingLinkInProgress: false,
        onboardingLink: payload,
      };
    case ONBOARDING_LINK_ERROR:
      console.error(payload);
      return {
        ...state,
        fetchOnboardingLinkError: payload,
        fetchOnboardingLinkInProgress: false,
      };

    case MERCHANT_SIGNUP_STATUS_REQUEST:
      return { ...state, fetchPaypalMerchantError: null, fetchPaypalMerchantInProgress: true };
    case MERCHANT_SIGNUP_STATUS_SUCCESS:
      return {
        ...state,
        fetchPaypalMerchantInProgress: false,
        paypalMerchant: payload,
      };
    case MERCHANT_SIGNUP_STATUS_ERROR:
      console.error(payload);
      return {
        ...state,
        fetchPaypalMerchantError: payload,
        fetchPaypalMerchantInProgress: false,
      };

    case AUTHORIZE_ORDER_REQUEST:
      return { ...state, authorizeOrderError: null, authorizeOrderInProgress: true };
    case AUTHORIZE_ORDER_SUCCESS:
      return {
        ...state,
        authorizeOrderInProgress: false,
        paypalAuthorizationData: payload,
      };
    case AUTHORIZE_ORDER_ERROR:
      console.error(payload);
      return {
        ...state,
        authorizeOrderError: payload,
        authorizeOrderInProgress: false,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const onboardingLinkRequest = () => ({ type: ONBOARDING_LINK_REQUEST });

export const onboardingLinkSuccess = payload => ({
  type: ONBOARDING_LINK_SUCCESS,
  payload,
});

export const onboardingLinkError = e => ({
  type: ONBOARDING_LINK_ERROR,
  payload: e,
  error: true,
});

export const merchantSignupStatusRequest = () => ({
  type: MERCHANT_SIGNUP_STATUS_REQUEST,
});

export const merchantSignupStatusSuccess = payload => ({
  type: MERCHANT_SIGNUP_STATUS_SUCCESS,
  payload,
});

export const merchantSignupStatusError = e => ({
  type: MERCHANT_SIGNUP_STATUS_ERROR,
  payload: e,
  error: true,
});

export const authorizeOrderRequest = () => ({
  type: AUTHORIZE_ORDER_REQUEST,
});

export const authorizeOrderSuccess = payload => ({
  type: AUTHORIZE_ORDER_SUCCESS,
  payload,
});

export const authorizeOrderError = e => ({
  type: AUTHORIZE_ORDER_ERROR,
  payload: e,
  error: true,
});

// ================ Thunks ================ //

/**
 * Creates a PayPal onboarding link for a seller.
 * @param {Object} params - Parameters for the onboarding link
 * @param {string} params.redirectUrl - The URL that the user will be redirected to after onboarding is complete
 * @returns {Promise<string>} A promise that resolves with the onboarding link if successful, and rejects with an error if not
 */
export const createSellerOnboardingLink = params => async (dispatch, getState, sdk) => {
  try {
    // Dispatch an action to indicate that we are making the request to create the onboarding link
    // This will trigger the loading state in the UI
    dispatch(onboardingLinkRequest());

    // Make the request to the PayPal API to get the onboarding link
    const response = await PayPalApi.getOnboardingLink(params);

    // If the request was successful, and the response contains a link to the onboarding process
    if (response.statusCode === StatusCode.CREATED) {
      // Find the onboarding link in the response
      const onboardingLink = response?.data.links.find(l => l.rel === 'action_url');

      // Dispatch an action to indicate that the onboarding link was successfully created
      // This will trigger the success state in the UI
      dispatch(onboardingLinkSuccess(onboardingLink));

      // Return the onboarding link
      return onboardingLink;
    } else {
      // If the request was not successful, convert the error to a storable error format
      const e = storableError(response);

      // Dispatch an action to indicate that there was an error creating the onboarding link
      // This will trigger the error state in the UI
      dispatch(onboardingLinkError(e));

      // Log the error for tracking/debugging purposes
      log.error(e, 'fetch-paypal-onboarding-links-failed');

      // Throw the error so that the caller can handle it
      throw e;
    }
  } catch (err) {
    // If there was an error, convert it to a storable error format
    const e = storableError(err);

    // Dispatch an action to indicate that there was an error creating the onboarding link
    // This will trigger the error state in the UI
    dispatch(onboardingLinkError(e));

    // Log the error for tracking/debugging purposes
    log.error(e, 'fetch-paypal-onboarding-links-failed');

    // Throw the error so that the caller can handle it
    throw e;
  }
};

/**
 * Fetch the status of the current user's PayPal merchant onboarding process.
 * The request will be sent to the PayPal API.
 * If the request is successful, the response will be dispatched as a success action.
 * If the request fails, an error action will be dispatched.
 * @return {Promise<Object>} The response from the PayPal API
 */
export const fetchPaypalMerchantSignupStatus = () => async dispatch => {
  try {
    dispatch(merchantSignupStatusRequest());

    const response = await PayPalApi.getMerchantOnboardingStatus();
    dispatch(merchantSignupStatusSuccess(response));
    return response;
  } catch (err) {
    const e = storableError(err);
    dispatch(merchantSignupStatusError(e));
    log.error(err, 'fetch-paypal-merchant-status-failed');
    throw e;
  }
};

/**
 * Authorize an approved PayPal order for the authenticated booking customer.
 */
export const authorizeOrder = (orderId, transactionId) => async dispatch => {
  dispatch(authorizeOrderRequest());

  try {
    const response = await PayPalApi.authorizeOrder(orderId, { transactionId });
    dispatch(authorizeOrderSuccess(response));
    return response;
  } catch (err) {
    const e = storableError(err);
    dispatch(authorizeOrderError(e));
    log.error(err, 'authorize-paypal-order-failed', e);
    throw e;
  }
};
